/**
 * Mock data for Dashboard Overview.
 * Used only when real API returns no data. Does not touch Redux or real fetch.
 * Real API data is always used when available.
 */

import type { RangeKey } from '@dataTypes/dashboard.types'
import type {
  AppointmentResponse,
  IOperatory,
} from '@dataTypes/appointment.type'
import moment from 'moment'
import { getRecallByProviderRows } from '@pages/Dashboard/RecallDetail/recallDetail.mockData'
import {
  getRecareKpi,
  parseRecareRatePercent,
} from '@pages/Dashboard/RecareDetail/recareDetail.mockData'
import {
  getTreatmentAcceptanceKpi,
  parseAcceptedRatePercent,
} from '@pages/Dashboard/TreatmentAcceptanceDetail/treatmentAcceptanceDetail.mockData'
import { PATIENT_FLOW_STATUS } from '@pages/Dashboard/PatientFlowDetail/patientFlowDetail.constants'

const MOCK_PROVIDER_ID = 'mock-provider-dashboard'
const MOCK_CLINIC_ID = 'mock-clinic-1'

const MOCK_OPERATORY_IDS = [
  'mock-operatory-1',
  'mock-operatory-2',
  'mock-operatory-3',
  'mock-operatory-4',
  'mock-operatory-5',
] as const

const MOCK_OPERATORY_NAMES = ['Room 1', 'Room 2', 'Room 3', 'Room 4', 'Room 5']
const MOCK_OPERATORY_COLORS = [
  '#185dd8',
  '#00ac69',
  '#f4a100',
  '#6900c7',
  '#e30059',
]

const mockClinic = {
  clinic_id: MOCK_CLINIC_ID,
  clinic_name: 'Mock Clinic',
  description: '',
}

export function getMockOperatoriesForOverview(): IOperatory[] {
  return MOCK_OPERATORY_IDS.map((operatory_id, i) => ({
    operatory_id,
    clinic_id: MOCK_CLINIC_ID,
    name: MOCK_OPERATORY_NAMES[i],
    color: MOCK_OPERATORY_COLORS[i],
    is_hidden: false,
    created_by: 'system',
    updated_by: 'system',
    providers: [],
    clinic: mockClinic,
  }))
}

function createMockAppointment(
  appointment_id: string,
  id: number,
  patientName: { first_name: string; last_name: string },
  appointmentType: string,
  dateStr: string,
  startTime: string,
  durationMinutes: number,
  operatoryId: string = MOCK_OPERATORY_IDS[0],
): AppointmentResponse {
  const date = moment(dateStr, 'MMMM D, YYYY')
  const start = moment(
    `${date.format('YYYY-MM-DD')} ${startTime}`,
    'YYYY-MM-DD HH:mm',
  )
  const end = moment(start).add(durationMinutes, 'minutes')

  return {
    appointment_id,
    patient_id: `mock-patient-${appointment_id}`,
    clinic_id: 'mock-clinic-1',
    operatory_id: operatoryId,
    insurance_id: null,
    provider_id: MOCK_PROVIDER_ID,
    provider_hygienist: null,
    appointment_type_id: `mock-type-${appointment_id}`,
    appointment_type_subcategory_id: `mock-subcat-${appointment_id}`,
    appt_status: 2,
    appt_date: date.format('YYYY-MM-DD'),
    appt_start: start.format('HH:mm'),
    appt_end: end.format('HH:mm'),
    duration: durationMinutes,
    is_recurring: false,
    recurring_visit_id: null,
    recurring_every: null,
    recurring_for: null,
    recurring_days: null,
    recurring_start_date: null,
    recurring_end_date: null,
    is_insured: false,
    tp_id: null,
    files: [],
    template_id: null,
    contact_via: [],
    payment_status: 0,
    is_read_only: false,
    created_by: 'system',
    updated_by: 'system',
    is_hidden: false,
    date_time_created_at: moment().toISOString(),
    date_time_updated_at: moment().toISOString(),
    insurance_plan_id: null,
    procedure_id: null,
    patient: {
      patient_id: `mock-patient-${appointment_id}`,
      first_name: patientName.first_name,
      last_name: patientName.last_name,
      cellphone: '+15551234567',
    },
    provider: {
      provider_id: MOCK_PROVIDER_ID,
      first_name: 'Jane',
      last_name: 'Smith',
      provider_color: {
        header: '#185dd8',
        background: '#b1d5fb',
        text_color: '#112850',
        label: 'Blue',
      },
    },
    appointmenttype: {
      appointment_type_id: `mock-type-${appointment_id}`,
      name: appointmentType,
    },
    procedure_logs: [],
    lab_orders: [],
    notes: [],
    Id: id,
    appointment_start_date: start.format('YYYY-MM-DD HH:mm:ss'),
    appointment_end_date: end.format('YYYY-MM-DD HH:mm:ss'),
  }
}

export function getMockAppointmentsForOverview(
  dateStr: string,
): AppointmentResponse[] {
  return [
    createMockAppointment(
      'mock-dash-1',
      1,
      { first_name: 'Alex', last_name: 'Johnson' },
      'Check-up',
      dateStr,
      '09:00',
      30,
      MOCK_OPERATORY_IDS[0],
    ),
    createMockAppointment(
      'mock-dash-2',
      2,
      { first_name: 'Sam', last_name: 'Williams' },
      'Cleaning',
      dateStr,
      '09:30',
      45,
      MOCK_OPERATORY_IDS[1],
    ),
    createMockAppointment(
      'mock-dash-3',
      3,
      { first_name: 'Jordan', last_name: 'Lee' },
      'New Patient',
      dateStr,
      '10:30',
      60,
      MOCK_OPERATORY_IDS[2],
    ),
    createMockAppointment(
      'mock-dash-4',
      4,
      { first_name: 'Casey', last_name: 'Brown' },
      'Follow-up',
      dateStr,
      '14:00',
      30,
      MOCK_OPERATORY_IDS[3],
    ),
    createMockAppointment(
      'mock-dash-5',
      5,
      { first_name: 'Morgan', last_name: 'Davis' },
      'Cleaning',
      dateStr,
      '15:00',
      45,
      MOCK_OPERATORY_IDS[4],
    ),
  ]
}

// ---------------------------------------------------------------------------
// Production — single source for Overview donuts, metrics, Production Detail
// ---------------------------------------------------------------------------

/** All money fields except avgAppt are in $K (e.g. 31.2 → $31.2K); 1Y totals use K-units (7860 → $7.86M via formatter). */
export type ProductionCoreNumbers = {
  grossK: number
  grossGoalK: number
  netK: number
  adjK: number
  insK: number
  patientK: number
  dentistRatio: number
  dentistK: number
  hygienistK: number
  avgAppt: number
}

const PRODUCTION_CORE_BY_RANGE: Record<RangeKey, ProductionCoreNumbers> = {
  '1D': {
    grossK: 31.2,
    grossGoalK: 50.6,
    netK: 28.4,
    adjK: 2.8,
    insK: 10.8,
    patientK: 16.1,
    dentistRatio: 0.62,
    dentistK: 21.8,
    hygienistK: 9.4,
    avgAppt: 420,
  },
  '1W': {
    grossK: 156,
    grossGoalK: 253,
    netK: 142,
    adjK: 14,
    insK: 54,
    patientK: 80.5,
    dentistRatio: 0.62,
    dentistK: 109,
    hygienistK: 47,
    avgAppt: 418,
  },
  '1M': {
    grossK: 655,
    grossGoalK: 1062,
    netK: 596,
    adjK: 59,
    insK: 226.8,
    patientK: 338.1,
    dentistRatio: 0.62,
    dentistK: 458,
    hygienistK: 197,
    avgAppt: 415,
  },
  '1Y': {
    grossK: 7860,
    grossGoalK: 12746,
    netK: 7160,
    adjK: 700,
    insK: 2724,
    patientK: 4057,
    dentistRatio: 0.62,
    dentistK: 5490,
    hygienistK: 2370,
    avgAppt: 418,
  },
  YTD: {
    grossK: 1970,
    grossGoalK: 3186,
    netK: 1790,
    adjK: 175,
    insK: 681,
    patientK: 1014,
    dentistRatio: 0.62,
    dentistK: 1370,
    hygienistK: 600,
    avgAppt: 416,
  },
}

export function getProductionCoreNumbers(
  range: RangeKey,
): ProductionCoreNumbers {
  return PRODUCTION_CORE_BY_RANGE[range]
}

/** Net goal (K) consistent with gross goal × (net/gross). */
export function getProductionNetGoalK(range: RangeKey): number {
  const c = PRODUCTION_CORE_BY_RANGE[range]
  return Math.round(c.grossGoalK * (c.netK / c.grossK) * 10) / 10
}

export function formatProductionDashboardMoney(v: number): string {
  const n = Math.round(v * 100) / 100
  if (n >= 1000) {
    return `$${(n / 1000).toFixed(2)}M`
  }
  const kStr = n.toFixed(2).replace(/\.?0+$/, '')
  return `$${kStr}K`
}

// ---------------------------------------------------------------------------
// Half-donut configs for Recare, Treatment acceptance,
// Chair time, Case acceptance
// ---------------------------------------------------------------------------

export type HalfDonutConfig = {
  primaryValue: number
  secondaryValue: number
  dentistRatio?: number
  slices?: Array<{ name: string; value: number; color: string }>
  valueFormatter: (v: number) => string
  valueSeparator?: string
}

// Recare — rate vs 100% goal (same `recareRate` as Recare detail)
export function getRecareDonutConfig(range: RangeKey): HalfDonutConfig {
  const kpi = getRecareKpi(range)
  const rate = parseRecareRatePercent(kpi.recareRate)
  const remainder = Math.max(0, 100 - rate)
  return {
    primaryValue: rate,
    secondaryValue: 100,
    slices: [
      { name: 'Recare rate', value: rate, color: 'var(--blue-400)' },
      { name: 'Remaining', value: remainder, color: 'var(--gray-200)' },
    ],
    valueFormatter: (val) => `${Math.round(val)}%`,
  }
}

/** Diagnosis row — treatment value half-donut (Insurance vs Patient), mock until API. */
const DIAGNOSIS_TREATMENT_K: Record<RangeKey, { ins: number; pat: number }> = {
  '1D': { ins: 8.4, pat: 7.2 },
  '1W': { ins: 42, pat: 36 },
  '1M': { ins: 168, pat: 144 },
  '1Y': { ins: 2016, pat: 1728 },
  YTD: { ins: 504, pat: 432 },
}

export function getDiagnosisDonutConfig(range: RangeKey): HalfDonutConfig {
  const { ins, pat } = DIAGNOSIS_TREATMENT_K[range]
  const total = Math.round((ins + pat) * 10) / 10
  const goal = Math.round(total * 1.15 * 100) / 100
  return {
    primaryValue: total,
    secondaryValue: goal,
    slices: [
      { name: 'Insurance', value: ins, color: 'var(--blue-400)' },
      { name: 'Patient', value: pat, color: 'var(--orange-300)' },
    ],
    valueFormatter: formatProductionDashboardMoney,
  }
}

/** Overview row chart — treatment value insurance vs patient (Figma). */
export function getDiagnosisOverviewChartConfig(
  range: RangeKey,
): HalfDonutConfig {
  const { ins, pat } = DIAGNOSIS_TREATMENT_K[range]
  return {
    primaryValue: ins,
    secondaryValue: pat,
    slices: [
      { name: 'Insurance', value: ins, color: 'var(--blue-400)' },
      { name: 'Patient', value: pat, color: 'var(--orange-300)' },
    ],
    valueFormatter: formatProductionDashboardMoney,
  }
}

// Treatment acceptance — acceptance rate vs remainder (same `acceptedRate` as detail)
export function getTreatmentAcceptanceDonutConfig(
  range: RangeKey,
): HalfDonutConfig {
  const kpi = getTreatmentAcceptanceKpi(range)
  const rate = parseAcceptedRatePercent(kpi.acceptedRate)
  const remainder = Math.max(0, 100 - rate)
  return {
    primaryValue: rate,
    secondaryValue: remainder,
    slices: [
      { name: 'Acceptance rate', value: rate, color: 'var(--blue-400)' },
      { name: 'Not accepted', value: remainder, color: 'var(--gray-200)' },
    ],
    valueFormatter: (val) => `${Math.round(val)}%`,
  }
}

export type TreatmentAcceptanceOverviewExtras = {
  txPresented: string
  txCompletedActual: string
  txCompletedTotal: string
}

const TA_OVERVIEW_EXTRAS: Record<RangeKey, TreatmentAcceptanceOverviewExtras> =
  {
    '1D': {
      txPresented: '12',
      txCompletedActual: '8',
      txCompletedTotal: '10',
    },
    '1W': {
      txPresented: '48',
      txCompletedActual: '31',
      txCompletedTotal: '38',
    },
    '1M': {
      txPresented: '186',
      txCompletedActual: '121',
      txCompletedTotal: '149',
    },
    '1Y': {
      txPresented: '2,208',
      txCompletedActual: '1,435',
      txCompletedTotal: '1,766',
    },
    YTD: {
      txPresented: '552',
      txCompletedActual: '359',
      txCompletedTotal: '442',
    },
  }

const TA_INS_PAT_CHART: Record<RangeKey, { ins: number; pat: number }> = {
  '1D': { ins: 58, pat: 42 },
  '1W': { ins: 57, pat: 43 },
  '1M': { ins: 56, pat: 44 },
  '1Y': { ins: 55, pat: 45 },
  YTD: { ins: 56, pat: 44 },
}

export function getTreatmentAcceptanceOverviewExtras(
  range: RangeKey,
): TreatmentAcceptanceOverviewExtras {
  return TA_OVERVIEW_EXTRAS[range]
}

/** Overview row chart — insurance vs patient split (Figma). */
export function getTreatmentAcceptanceOverviewChartConfig(
  range: RangeKey,
): HalfDonutConfig {
  const { ins, pat } = TA_INS_PAT_CHART[range]
  return {
    primaryValue: ins,
    secondaryValue: pat,
    slices: [
      { name: 'Insurance', value: ins, color: 'var(--blue-400)' },
      { name: 'Patient', value: pat, color: 'var(--orange-300)' },
    ],
    valueFormatter: (val) => `${val}%`,
  }
}

// Chair time — "Efficiency rate" (%)
const CHAIR_TIME_DONUT_BY_RANGE: Record<
  RangeKey,
  {
    efficiency: number
    goal: number
    scheduled: number
    available: number
    totalHrs: string
    schedHrs: string
    openHrs: string
  }
> = {
  '1D': {
    efficiency: 89,
    goal: 75,
    scheduled: 89,
    available: 11,
    totalHrs: '40 hrs',
    schedHrs: '34 hrs',
    openHrs: '6 hrs',
  },
  '1W': {
    efficiency: 87,
    goal: 75,
    scheduled: 87,
    available: 13,
    totalHrs: '200 hrs',
    schedHrs: '174 hrs',
    openHrs: '26 hrs',
  },
  '1M': {
    efficiency: 88,
    goal: 75,
    scheduled: 88,
    available: 12,
    totalHrs: '840 hrs',
    schedHrs: '739 hrs',
    openHrs: '101 hrs',
  },
  '1Y': {
    efficiency: 89,
    goal: 75,
    scheduled: 89,
    available: 11,
    totalHrs: '10,080 hrs',
    schedHrs: '8,971 hrs',
    openHrs: '1,109 hrs',
  },
  YTD: {
    efficiency: 88,
    goal: 75,
    scheduled: 88,
    available: 12,
    totalHrs: '2,520 hrs',
    schedHrs: '2,218 hrs',
    openHrs: '302 hrs',
  },
}

export type ChairTimeConfig = HalfDonutConfig & {
  totalHrs: string
  schedHrs: string
  openHrs: string
}

export function getChairTimeConfig(range: RangeKey): ChairTimeConfig {
  const v = CHAIR_TIME_DONUT_BY_RANGE[range]
  return {
    primaryValue: v.efficiency,
    secondaryValue: v.goal,
    valueSeparator: ' vs ',
    slices: [
      { name: 'Scheduled', value: v.scheduled, color: 'var(--blue-400)' },
      { name: 'Available', value: v.available, color: 'var(--orange-300)' },
    ],
    valueFormatter: (val) => `${val}%`,
    totalHrs: v.totalHrs,
    schedHrs: v.schedHrs,
    openHrs: v.openHrs,
  }
}

// Case acceptance — "Avg case acceptance" (%)
const CASE_ACCEPTANCE_DONUT_BY_RANGE: Record<
  RangeKey,
  {
    primary: number
    secondary: number
    ins: number
    patient: number
    acceptedVal: string
    sameDayVal: string
    pendingVal: string
  }
> = {
  '1D': {
    primary: 78,
    secondary: 80,
    ins: 40,
    patient: 60,
    acceptedVal: '$4.2K',
    sameDayVal: '$1.3K',
    pendingVal: '$5.2K',
  },
  '1W': {
    primary: 79,
    secondary: 80,
    ins: 41,
    patient: 59,
    acceptedVal: '$21K',
    sameDayVal: '$6.5K',
    pendingVal: '$26K',
  },
  '1M': {
    primary: 78,
    secondary: 80,
    ins: 40,
    patient: 60,
    acceptedVal: '$88.2K',
    sameDayVal: '$27.3K',
    pendingVal: '$109.2K',
  },
  '1Y': {
    primary: 80,
    secondary: 80,
    ins: 39,
    patient: 61,
    acceptedVal: '$1.06M',
    sameDayVal: '$328K',
    pendingVal: '$1.31M',
  },
  YTD: {
    primary: 79,
    secondary: 80,
    ins: 40,
    patient: 60,
    acceptedVal: '$264.6K',
    sameDayVal: '$81.9K',
    pendingVal: '$327.6K',
  },
}

export type CaseAcceptanceConfig = HalfDonutConfig & {
  acceptedVal: string
  sameDayVal: string
  pendingVal: string
}

export function getCaseAcceptanceConfig(range: RangeKey): CaseAcceptanceConfig {
  const v = CASE_ACCEPTANCE_DONUT_BY_RANGE[range]
  const notAccepted = Math.max(0, 100 - v.primary)
  return {
    primaryValue: v.primary,
    secondaryValue: v.secondary,
    valueSeparator: ' vs ',
    slices: [
      { name: 'Acceptance rate', value: v.primary, color: 'var(--blue-400)' },
      {
        name: 'Not accepted',
        value: notAccepted,
        color: 'var(--orange-300)',
      },
    ],
    valueFormatter: (val) => `${val}%`,
    acceptedVal: v.acceptedVal,
    sameDayVal: v.sameDayVal,
    pendingVal: v.pendingVal,
  }
}

/** Incomplete treatment plan table */
export type IncompleteTreatmentPlanRow = {
  id: string
  patientName: string
  lastVisit: string
  actions: string
}

export const INCOMPLETE_TREATMENT_PLAN_COLUMNS = [
  { key: 'patientName', label: 'Patient name', sortable: true },
  { key: 'lastVisit', label: 'Last visit', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
] as const

const ALL_INCOMPLETE_TX_ROWS: IncompleteTreatmentPlanRow[] = [
  { id: '1', patientName: 'John Doe', lastVisit: '03/18/2026', actions: '' },
  { id: '2', patientName: 'Jane Smith', lastVisit: '03/12/2026', actions: '' },
  {
    id: '3',
    patientName: 'Alice Johnson',
    lastVisit: '03/03/2026',
    actions: '',
  },
  {
    id: '4',
    patientName: 'Michael Brown',
    lastVisit: '02/25/2026',
    actions: '',
  },
  { id: '5', patientName: 'Emily Davis', lastVisit: '02/12/2026', actions: '' },
  { id: '6', patientName: 'Chris Lee', lastVisit: '01/30/2026', actions: '' },
  {
    id: '7',
    patientName: 'Sarah Wilson',
    lastVisit: '01/15/2026',
    actions: '',
  },
  {
    id: '8',
    patientName: 'James Thompson',
    lastVisit: '12/20/2025',
    actions: '',
  },
]

const INCOMPLETE_TX_ROW_COUNT: Record<RangeKey, number> = {
  '1D': 2,
  '1W': 4,
  '1M': 6,
  '1Y': 8,
  YTD: 7,
}

export function getIncompleteTreatmentPlanData(
  range: RangeKey = '1D',
): IncompleteTreatmentPlanRow[] {
  return ALL_INCOMPLETE_TX_ROWS.slice(0, INCOMPLETE_TX_ROW_COUNT[range])
}

/** Open treatment patients table */
export type OpenTreatmentPatientsRow = {
  id: string
  patientName: string
  unschedAmount: string
  actions: string
}

export const OPEN_TREATMENT_PATIENTS_COLUMNS = [
  { key: 'patientName', label: 'Patient name', sortable: true },
  { key: 'unschedAmount', label: 'Unsched $', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
] as const

const OPEN_TX_AMOUNTS: Record<RangeKey, { amounts: string[]; count: number }> =
  {
    '1D': {
      amounts: [
        '$840',
        '$650',
        '$1,280',
        '$2,140',
        '$1,040',
        '$800',
        '$560',
        '$320',
      ],
      count: 2,
    },
    '1W': {
      amounts: [
        '$1,680',
        '$1,250',
        '$2,560',
        '$3,240',
        '$1,840',
        '$800',
        '$960',
        '$620',
      ],
      count: 4,
    },
    '1M': {
      amounts: [
        '$2,840',
        '$1,850',
        '$3,280',
        '$5,340',
        '$1,040',
        '$800',
        '$1,560',
        '$920',
      ],
      count: 6,
    },
    '1Y': {
      amounts: [
        '$14,280',
        '$9,250',
        '$18,480',
        '$28,140',
        '$8,240',
        '$4,800',
        '$7,560',
        '$4,420',
      ],
      count: 8,
    },
    YTD: {
      amounts: [
        '$6,840',
        '$4,650',
        '$9,280',
        '$12,540',
        '$4,040',
        '$2,800',
        '$3,560',
        '$2,120',
      ],
      count: 7,
    },
  }

const OPEN_TX_NAMES = [
  'John Doe',
  'Jane Smith',
  'Alice Johnson',
  'Michael Brown',
  'Emily Davis',
  'Chris Lee',
  'Sarah Wilson',
  'James Thompson',
]

export function getOpenTreatmentPatientsData(
  range: RangeKey = '1D',
): OpenTreatmentPatientsRow[] {
  const { amounts, count } = OPEN_TX_AMOUNTS[range]
  return OPEN_TX_NAMES.slice(0, count).map((name, i) => ({
    id: String(i + 1),
    patientName: name,
    unschedAmount: amounts[i],
    actions: '',
  }))
}

/** Patient Flow Tracker columns — labels match appointment flow statuses */
export type PatientFlowColumn = {
  header: string
  count: string
  names: string[]
}

const ALL_FLOW_NAMES = [
  'Andrew K. Smith',
  'Sarah J. Thompson',
  'Emily R. Davis',
  'Sandra L. Johnson',
  'Michael A. Johnson',
  'Sophia A. Lee',
  'Jessica T. Lee',
  'David L. Smith',
  'Daniel P. Kim',
  'Michael A. Smith',
]

export function getPatientFlowData(
  range: RangeKey = '1D',
): PatientFlowColumn[] {
  const cfg: Record<
    RangeKey,
    { sched: number; checkedIn: number; inTx: number; checkedOut: number }
  > = {
    '1D': { sched: 10, checkedIn: 4, inTx: 4, checkedOut: 3 },
    '1W': { sched: 160, checkedIn: 22, inTx: 18, checkedOut: 120 },
    '1M': { sched: 672, checkedIn: 28, inTx: 22, checkedOut: 622 },
    '1Y': { sched: 8064, checkedIn: 30, inTx: 24, checkedOut: 8010 },
    YTD: { sched: 2016, checkedIn: 28, inTx: 20, checkedOut: 1968 },
  }
  const { sched, checkedIn, inTx, checkedOut } = cfg[range]
  return [
    {
      header: PATIENT_FLOW_STATUS.CONFIRMED,
      count: String(sched),
      names: ALL_FLOW_NAMES.slice(0, Math.min(sched, ALL_FLOW_NAMES.length)),
    },
    {
      header: PATIENT_FLOW_STATUS.CHECK_IN,
      count: String(checkedIn),
      names: ALL_FLOW_NAMES.slice(0, checkedIn),
    },
    {
      header: PATIENT_FLOW_STATUS.IN_TREATMENT,
      count: String(inTx),
      names: ALL_FLOW_NAMES.slice(4, 4 + inTx),
    },
    {
      header: PATIENT_FLOW_STATUS.CHECK_OUT,
      count: String(checkedOut),
      names: ALL_FLOW_NAMES.slice(7, 7 + checkedOut),
    },
  ]
}

export type LineChartConfig = {
  data: Array<{ label: string; value: number }>
  yDomain: [number, number]
  yTicks: number[]
}

/** Overview No-show card — lost chair time (hrs) line chart. */
export function getOverviewNoShowLineChart(range: RangeKey): LineChartConfig {
  switch (range) {
    case '1D':
      return {
        data: [
          { label: '8 AM', value: 0 },
          { label: '9 AM', value: 2.75 },
          { label: '10 AM', value: 0 },
          { label: '11 AM', value: 0 },
          { label: '12 PM', value: 1 },
          { label: '1 PM', value: 0 },
          { label: '2 PM', value: 0 },
          { label: '3 PM', value: 0 },
          { label: '4 PM', value: 0.75 },
          { label: '5 PM', value: 0 },
          { label: '6 PM', value: 0 },
        ],
        yDomain: [0, 3],
        yTicks: [0, 1, 2, 3],
      }
    case '1W':
      return {
        data: [
          { label: 'Mon', value: 2.5 },
          { label: 'Tue', value: 3.2 },
          { label: 'Wed', value: 1.8 },
          { label: 'Thu', value: 2.9 },
          { label: 'Fri', value: 2.1 },
          { label: 'Sat', value: 0.5 },
          { label: 'Sun', value: 0 },
        ],
        yDomain: [0, 4],
        yTicks: [0, 1, 2, 3, 4],
      }
    case '1M':
      return {
        data: [
          { label: 'Wk 1', value: 18 },
          { label: 'Wk 2', value: 22 },
          { label: 'Wk 3', value: 20 },
          { label: 'Wk 4', value: 25 },
        ],
        yDomain: [0, 30],
        yTicks: [0, 10, 20, 30],
      }
    case '1Y':
      return {
        data: [
          { label: 'Jan', value: 72 },
          { label: 'Feb', value: 68 },
          { label: 'Mar', value: 85 },
          { label: 'Apr', value: 90 },
          { label: 'May', value: 78 },
          { label: 'Jun', value: 95 },
          { label: 'Jul', value: 88 },
          { label: 'Aug', value: 82 },
          { label: 'Sep', value: 91 },
          { label: 'Oct', value: 87 },
          { label: 'Nov', value: 79 },
          { label: 'Dec', value: 94 },
        ],
        yDomain: [0, 100],
        yTicks: [0, 25, 50, 75, 100],
      }
    case 'YTD':
      return {
        data: [
          { label: 'Jan', value: 72 },
          { label: 'Feb', value: 68 },
          { label: 'Mar', value: 85 },
        ],
        yDomain: [0, 100],
        yTicks: [0, 25, 50, 75, 100],
      }
  }
}

/** Overview Same-day cancellation card — open chair time (hrs) line chart. */
export function getOverviewSameDayCancellationLineChart(
  range: RangeKey,
): LineChartConfig {
  switch (range) {
    case '1D':
      return {
        data: [
          { label: '8 AM', value: 0 },
          { label: '9 AM', value: 0 },
          { label: '10 AM', value: 0 },
          { label: '11 AM', value: 0.5 },
          { label: '12 PM', value: 0 },
          { label: '1 PM', value: 0 },
          { label: '2 PM', value: 0.75 },
          { label: '3 PM', value: 0 },
          { label: '4 PM', value: 0 },
          { label: '5 PM', value: 0 },
          { label: '6 PM', value: 0 },
        ],
        yDomain: [0, 3],
        yTicks: [0, 1, 2, 3],
      }
    case '1W':
      return {
        data: [
          { label: 'Mon', value: 1.5 },
          { label: 'Tue', value: 0 },
          { label: 'Wed', value: 0.85 },
          { label: 'Thu', value: 0 },
          { label: 'Fri', value: 0 },
          { label: 'Sat', value: 0 },
          { label: 'Sun', value: 0 },
        ],
        yDomain: [0, 3],
        yTicks: [0, 1, 2, 3],
      }
    case '1M':
      return {
        data: [
          { label: 'Wk 1', value: 12 },
          { label: 'Wk 2', value: 15 },
          { label: 'Wk 3', value: 11 },
          { label: 'Wk 4', value: 13 },
        ],
        yDomain: [0, 20],
        yTicks: [0, 5, 10, 15, 20],
      }
    case '1Y':
      return {
        data: [
          { label: 'Jan', value: 48 },
          { label: 'Feb', value: 52 },
          { label: 'Mar', value: 44 },
          { label: 'Apr', value: 50 },
          { label: 'May', value: 46 },
          { label: 'Jun', value: 54 },
          { label: 'Jul', value: 50 },
          { label: 'Aug', value: 48 },
          { label: 'Sep', value: 42 },
          { label: 'Oct', value: 46 },
          { label: 'Nov', value: 50 },
          { label: 'Dec', value: 44 },
        ],
        yDomain: [0, 60],
        yTicks: [0, 15, 30, 45, 60],
      }
    case 'YTD':
      return {
        data: [
          { label: 'Jan', value: 48 },
          { label: 'Feb', value: 52 },
          { label: 'Mar', value: 44 },
        ],
        yDomain: [0, 60],
        yTicks: [0, 15, 30, 45, 60],
      }
  }
}

/** Line chart data keyed by time range. */
export function getOverviewLineChartByRange(range: RangeKey): LineChartConfig {
  switch (range) {
    case '1D':
      return {
        data: [
          { label: '8 AM', value: 1 },
          { label: '9 AM', value: 2 },
          { label: '10 AM', value: 3 },
          { label: '11 AM', value: 2 },
          { label: '12 PM', value: 1 },
          { label: '1 PM', value: 2 },
          { label: '2 PM', value: 3 },
          { label: '3 PM', value: 2 },
          { label: '4 PM', value: 1 },
          { label: '5 PM', value: 1 },
          { label: '6 PM', value: 0 },
        ],
        yDomain: [0, 4],
        yTicks: [0, 1, 2, 3, 4],
      }
    case '1W':
      return {
        data: [
          { label: 'Mon', value: 3 },
          { label: 'Tue', value: 5 },
          { label: 'Wed', value: 4 },
          { label: 'Thu', value: 6 },
          { label: 'Fri', value: 4 },
          { label: 'Sat', value: 2 },
          { label: 'Sun', value: 1 },
        ],
        yDomain: [0, 8],
        yTicks: [0, 2, 4, 6, 8],
      }
    case '1M':
      return {
        data: [
          { label: 'Wk 1', value: 18 },
          { label: 'Wk 2', value: 22 },
          { label: 'Wk 3', value: 20 },
          { label: 'Wk 4', value: 25 },
        ],
        yDomain: [0, 30],
        yTicks: [0, 10, 20, 30],
      }
    case '1Y':
      return {
        data: [
          { label: 'Jan', value: 72 },
          { label: 'Feb', value: 68 },
          { label: 'Mar', value: 85 },
          { label: 'Apr', value: 90 },
          { label: 'May', value: 78 },
          { label: 'Jun', value: 95 },
          { label: 'Jul', value: 88 },
          { label: 'Aug', value: 82 },
          { label: 'Sep', value: 91 },
          { label: 'Oct', value: 87 },
          { label: 'Nov', value: 79 },
          { label: 'Dec', value: 94 },
        ],
        yDomain: [0, 100],
        yTicks: [0, 25, 50, 75, 100],
      }
    case 'YTD':
      return {
        data: [
          { label: 'Jan', value: 72 },
          { label: 'Feb', value: 68 },
          { label: 'Mar', value: 85 },
        ],
        yDomain: [0, 100],
        yTicks: [0, 25, 50, 75, 100],
      }
  }
}

/** @deprecated Use getOverviewLineChartByRange('1D') instead */
export function getOverviewLineChartByTimeData(): Array<{
  label: string
  value: number
}> {
  return getOverviewLineChartByRange('1D').data
}

/** @deprecated Use getOverviewLineChartByRange('1W') instead */
export function getOverviewLineChartByDayData(): Array<{
  label: string
  value: number
}> {
  return getOverviewLineChartByRange('1W').data
}

// ---------------------------------------------------------------------------
// Recall table
// ---------------------------------------------------------------------------

export const RECALL_TABLE_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'completed', label: 'Completed', sortable: true },
  { key: 'overdue', label: 'Overdue', sortable: true },
  { key: 'scheduled', label: 'Scheduled', sortable: true },
  { key: 'recallRate', label: 'Recall rate', sortable: true },
]

/** By-provider recall rows — same data as Recall detail (`getRecallByProviderRows`). */
export function getRecallTableRows(range: RangeKey = '1D'): Array<{
  id: string
  name: string
  overdue: string
  recallRate: string
  completed: string
  scheduled: string
}> {
  return getRecallByProviderRows(range).map((row) => ({
    id: row.id,
    name: row.provider,
    completed: String(row.completed),
    overdue: String(row.overdue),
    scheduled: String(row.scheduled),
    recallRate: row.recallRate,
  }))
}

/** @deprecated Use getRecallTableRows(range) instead */
export const RECALL_TABLE_ROWS = getRecallTableRows('1M')

// ---------------------------------------------------------------------------
// Provider utilization table
// ---------------------------------------------------------------------------

export const PROVIDER_UTILIZATION_COLUMNS = [
  { key: 'name', label: 'Provider', sortable: true },
  { key: 'utilization', label: 'Utilization', sortable: true },
  { key: 'idleTime', label: 'Idle time', sortable: true },
  { key: 'bookedTime', label: 'Booked time', sortable: true },
]

type ProviderBase = {
  id: string
  name: string
  utilization: string
  idlePct: number
  bookedPct: number
}

const PROVIDER_BASES: ProviderBase[] = [
  {
    id: '1',
    name: 'John Smith',
    utilization: '85%',
    idlePct: 0.12,
    bookedPct: 0.88,
  },
  {
    id: '2',
    name: 'Jane Doe',
    utilization: '67%',
    idlePct: 0.33,
    bookedPct: 0.67,
  },
  {
    id: '3',
    name: 'Emily Johnson',
    utilization: '75%',
    idlePct: 0.25,
    bookedPct: 0.75,
  },
  {
    id: '4',
    name: 'Michael Brown',
    utilization: '82%',
    idlePct: 0.18,
    bookedPct: 0.82,
  },
  {
    id: '5',
    name: 'David Martinez',
    utilization: '89%',
    idlePct: 0.11,
    bookedPct: 0.89,
  },
  {
    id: '6',
    name: 'Sarah Wilson',
    utilization: '95%',
    idlePct: 0.05,
    bookedPct: 0.95,
  },
  {
    id: '7',
    name: 'James Thompson',
    utilization: '90%',
    idlePct: 0.1,
    bookedPct: 0.9,
  },
  {
    id: '8',
    name: 'Lisa Anderson',
    utilization: '79%',
    idlePct: 0.21,
    bookedPct: 0.79,
  },
  {
    id: '9',
    name: 'Robert Chen',
    utilization: '88%',
    idlePct: 0.12,
    bookedPct: 0.88,
  },
  {
    id: '10',
    name: 'Amanda Patel',
    utilization: '72%',
    idlePct: 0.28,
    bookedPct: 0.72,
  },
]

const TOTAL_HRS_BY_RANGE: Record<RangeKey, number> = {
  '1D': 8,
  '1W': 40,
  '1M': 168,
  '1Y': 2016,
  YTD: 504,
}

function fmtHrs(h: number): string {
  if (h >= 1000) return `${(h / 1000).toFixed(1)}K hrs`
  return `${h % 1 === 0 ? h : h.toFixed(1)} hrs`
}

export function getProviderUtilizationRows(
  range: RangeKey = '1D',
  providerNames?: string[],
): Array<{
  id: string
  name: string
  utilization: string
  idleTime: string
  bookedTime: string
}> {
  const total = TOTAL_HRS_BY_RANGE[range]

  const buildRow = (
    base: ProviderBase,
    index: number,
    name: string,
  ): {
    id: string
    name: string
    utilization: string
    idleTime: string
    bookedTime: string
  } => ({
    id: String(index + 1),
    name,
    utilization: base.utilization,
    idleTime: fmtHrs(Math.round(total * base.idlePct * 10) / 10),
    bookedTime: fmtHrs(Math.round(total * base.bookedPct * 10) / 10),
  })

  if (providerNames && providerNames.length > 0) {
    return providerNames.map((name, index) =>
      buildRow(PROVIDER_BASES[index % PROVIDER_BASES.length], index, name),
    )
  }

  return PROVIDER_BASES.map((p, index) => buildRow(p, index, p.name))
}

/** @deprecated Use getProviderUtilizationRows(range) instead */
export const PROVIDER_UTILIZATION_ROWS = getProviderUtilizationRows('1D')

// ---------------------------------------------------------------------------
// Tasks & Alerts counts
// ---------------------------------------------------------------------------

export type TaskCounts = {
  alerts: number
  checklist: number
  production: number
  scheduling: number
  followUps: number
  collections: number
  performance: number
}

const TASK_COUNTS_BY_RANGE: Record<RangeKey, TaskCounts> = {
  '1D': {
    alerts: 3,
    checklist: 0,
    production: 0,
    scheduling: 3,
    followUps: 0,
    collections: 0,
    performance: 0,
  },
  '1W': {
    alerts: 8,
    checklist: 0,
    production: 2,
    scheduling: 6,
    followUps: 4,
    collections: 2,
    performance: 1,
  },
  '1M': {
    alerts: 12,
    checklist: 0,
    production: 5,
    scheduling: 9,
    followUps: 8,
    collections: 6,
    performance: 3,
  },
  '1Y': {
    alerts: 24,
    checklist: 0,
    production: 8,
    scheduling: 12,
    followUps: 15,
    collections: 10,
    performance: 5,
  },
  YTD: {
    alerts: 18,
    checklist: 0,
    production: 6,
    scheduling: 10,
    followUps: 10,
    collections: 7,
    performance: 4,
  },
}

export function getTaskCounts(range: RangeKey = '1D'): TaskCounts {
  return TASK_COUNTS_BY_RANGE[range]
}
